/**
 * Two-Factor Authentication Store (Story 1.7)
 */
import { create } from "zustand";
import { twoFactorService } from "../services/twoFactorService";

interface SetupData {
  qrCode: string;
  manualKey: string;
}

interface TwoFactorStatus {
  enabled: boolean;
  backupCodesRemaining: number;
  gracePeriodStarted: boolean;
  graceDaysRemaining: number | null;
}

interface TwoFactorState {
  setupData: SetupData | null;
  backupCodes: string[];
  status: TwoFactorStatus | null;
  loading: boolean;
  error: string | null;

  // Actions
  startSetup: () => Promise<void>;
  verifyAndEnable: (code: string) => Promise<string[]>;
  disable: (code: string) => Promise<void>;
  fetchStatus: () => Promise<void>;
  regenerateBackupCodes: () => Promise<string[]>;
  clearSetupData: () => void;
  clearBackupCodes: () => void;
  clearError: () => void;
}

export const useTwoFactorStore = create<TwoFactorState>()((set, get) => ({
  setupData: null,
  backupCodes: [],
  status: null,
  loading: false,
  error: null,

  startSetup: async () => {
    set({ loading: true, error: null });
    try {
      const data = await twoFactorService.startSetup();
      set({
        setupData: data,
        loading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to start 2FA setup";
      set({ error: message, loading: false });
      throw err;
    }
  },

  verifyAndEnable: async (code: string) => {
    set({ loading: true, error: null });
    try {
      const backupCodes = await twoFactorService.verifyAndEnable(code);
      set({
        backupCodes,
        setupData: null,
        loading: false,
        status: get().status ? { ...get().status!, enabled: true, backupCodesRemaining: 10 } : null,
      });
      return backupCodes;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to enable 2FA";
      set({ error: message, loading: false });
      throw err;
    }
  },

  disable: async (code: string) => {
    set({ loading: true, error: null });
    try {
      await twoFactorService.disable(code);
      set({
        loading: false,
        status: get().status ? { ...get().status!, enabled: false, backupCodesRemaining: 0 } : null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to disable 2FA";
      set({ error: message, loading: false });
      throw err;
    }
  },

  fetchStatus: async () => {
    set({ loading: true, error: null });
    try {
      const status = await twoFactorService.getStatus();
      set({ status, loading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch 2FA status";
      set({ error: message, loading: false });
      throw err;
    }
  },

  regenerateBackupCodes: async () => {
    set({ loading: true, error: null });
    try {
      const newBackupCodes = await twoFactorService.regenerateBackupCodes();
      set({
        backupCodes: newBackupCodes,
        loading: false,
        status: get().status ? { ...get().status!, backupCodesRemaining: 10 } : null,
      });
      // Refresh status to get accurate count
      await get().fetchStatus();
      return newBackupCodes;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to regenerate backup codes";
      set({ error: message, loading: false });
      throw err;
    }
  },

  clearSetupData: () => {
    set({ setupData: null });
  },

  clearBackupCodes: () => {
    set({ backupCodes: [] });
  },

  clearError: () => {
    set({ error: null });
  },
}));
